// ShuttleModeOverlay — Wave 14dt
// ===============================
//
// Self-contained Overlay-Wrapper für den Shuttle-Mode auf der Map.
// MapPage rendert <ShuttleModeOverlay /> einmal; alle Logik intern.
//
// Aktivierung: URL-Param `?mode=shuttle` (RunsPage navigiert dorthin).
//
// Beim Aktivieren:
//   - alle Trail-Polylines per setLayoutProperty auf 'none' (re-apply auf
//     'styledata' und 'idle')
//   - 5 Area-Badges (PL/KA/IS/ME/BN) als runde DOM-Marker rendern
//   - Header oben mit "DH/Shuttle mode"-Pill + Rotes-X-Exit
//
// Tap auf ein Badge (Phase 3c):
//   - Runs der Area aus dem IDB-Cache holen (syncRunCache + Filter)
//   - GeoJSON-Source `shuttle-active-runs` mit den Run-Tracks
//   - Line-Layer in sky-500 darüber rendern
//   - fitBounds zur kombinierten BBox
//   - Bottom-Carousel: horizontal-scrollbare Run-Cards mit RIDE-Button
//
// Carousel-Design: snap-x scroll auf Container, Card = 80% Viewport-Breite,
// 8px Gap → Tinder-style swipe. Bei vielen Runs (3+) sieht man den Anfang
// der nächsten Card am rechten Rand → klare Swipe-Affordance.
//
// RIDE-Button: navigiert zu `/runs/:id` (existierende RunDetailPage hat
// dort den vollständigen RIDE-Flow). Das hier ist nicht das End-of-Funnel,
// sondern eine Sprung-Brücke ins existierende Routing.
//
// Exit:
//   - Rotes X oben rechts → URL-Param raus, Trails wieder sichtbar
//   - Rotes X auf Carousel → nur activeAreaCode raus (Mode bleibt aktiv)

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { Map as MLMap } from 'maplibre-gl';
import type { ShuttleArea } from '@adtu/shared';
import { DIFFICULTY_LABEL, difficultySwatchBackground, type Difficulty } from '@adtu/shared';
import { mapBus } from '../lib/map/mapBus';
import { useShuttleAreas } from '../lib/useShuttleAreas';
import { syncRunCache, type CachedRun } from '../lib/runs';
import { ShuttleAreaLayer } from './ShuttleAreaLayer';

// Trail-Layer-IDs die im Shuttle-Mode versteckt werden.
// Wave 14dt-fix1: Korrigierte Liste — die echten IDs aus
// lib/map/style.ts + MapView.tsx + showOnMapTours.ts. Vorherige Liste
// matchte nichts → Trails blieben im Shuttle-Mode sichtbar.
const TRAIL_LAYER_IDS = [
  // Basis-Trail-Linien aus style.ts (Wave 14be).
  'trails-shadow',
  'trails-halo',
  'trails-undercoat',
  'trails-line-solid',
  'trails-line-dashed',
  'trails-label',
  'trails-hit',
  // Start/End-Markers + Selection-Highlights aus MapView.tsx.
  'trail-endpoints-all-start',
  'trail-endpoints-all-end',
  'trail-selection-aura',
  'trail-selection-arrows',
  'trail-selection-start',
  'trail-selection-end',
  // Featured-Tours-Linien aus showOnMapTours.ts.
  'adt-show-on-map-tours-line',
  'adt-show-on-map-tours-casing',
  'adt-show-on-map-km-markers-symbols',
];

// Dark-Background-Style für die Carousel-Cards + Banner. Inline statt
// `bg-turbo-800/95` weil das Light-Mode-CSS-@layer-Utility den turbo-800
// auf ein helles Grau überschreibt → text-white wird quasi unsichtbar.
// rgba(15,23,42,0.96) = slate-900 mit 96% Alpha, dunkel in beiden Modes.
const DARK_PANEL_BG: CSSProperties = {
  background: 'rgba(15, 23, 42, 0.96)',
  color: '#ffffff',
};

// Source + Layer für die Run-Polylines der aktiven Area.
const ACTIVE_RUNS_SOURCE = 'shuttle-active-runs';
const ACTIVE_RUNS_LAYER = 'shuttle-active-runs-line';
const ACTIVE_RUNS_LAYER_CASING = 'shuttle-active-runs-line-casing';

function formatKm(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

export function ShuttleModeOverlay() {
  const location = useLocation();
  const navigate = useNavigate();

  const isShuttleMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('mode') === 'shuttle';
  }, [location.search]);

  const [map, setMap] = useState<MLMap | null>(mapBus.get());
  useEffect(() => mapBus.subscribe(setMap), []);

  const { areas, loading } = useShuttleAreas();
  const [activeAreaCode, setActiveAreaCode] = useState<string | null>(null);
  const [allRuns, setAllRuns] = useState<CachedRun[]>([]);
  // Wave 14dt-fix4: Preview-Run — Card im Carousel-Center → wird auf der
  // Karte hervorgehoben (orange, dickere Line). Damit kann der User
  // swipen + visuell vergleichen BEVOR er SHOW oder RIDE drückt.
  const [previewRunId, setPreviewRunId] = useState<string | null>(null);
  const carouselRef = useRef<HTMLUListElement | null>(null);

  // Runs einmal laden wenn der Mode aktiv wird (oder die View frisch öffnet).
  // syncRunCache ist idempotent — falls schon gecacht, kommt der IDB-Inhalt
  // ohne Netz-Roundtrip.
  useEffect(() => {
    if (!isShuttleMode) return;
    let cancelled = false;
    syncRunCache({ domParser: new DOMParser() })
      .then((list) => {
        if (!cancelled) setAllRuns(list);
      })
      .catch((err) => {
        console.warn('[shuttle] syncRunCache failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [isShuttleMode]);

  // activeArea-State beim Verlassen des Shuttle-Mode hart zurücksetzen.
  useEffect(() => {
    if (!isShuttleMode) setActiveAreaCode(null);
  }, [isShuttleMode]);

  // Wave 14dt-fix5/6: Beim Eintritt in den Shuttle-Mode auf alle 5 Areas
  // rauszoomen. Wave 14dt-fix6: jetzt mit setTimeout(400ms) damit der
  // fitBounds NACH dem GPS-auto-follow (das beim Map-Mount kurz centert)
  // läuft, sonst wird unser fitBounds sofort übersteuert.
  // map.stop() canceled jede in-flight Camera-Animation davor.
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!isShuttleMode) {
      didInitialFitRef.current = false;
      return;
    }
    if (didInitialFitRef.current) return;
    if (!map || areas.length === 0 || activeAreaCode) return;
    const lons = areas.map((a) => a.lon);
    const lats = areas.map((a) => a.lat);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    didInitialFitRef.current = true;
    const doFit = () => {
      try {
        try {
          map.stop(); // jede in-flight Camera-Animation canceln
        } catch {
          /* noop */
        }
        map.fitBounds(
          [
            [minLon, minLat],
            [maxLon, maxLat],
          ],
          {
            padding: { top: 110, bottom: 80, left: 60, right: 60 },
            duration: 900,
            maxZoom: 12,
          },
        );
      } catch (err) {
        console.warn('[shuttle] initial fitBounds failed', err);
      }
    };
    // Erst-Versuch fast sofort + Wiederholung nach 400ms — der GPS-Auto-
    // Follow von LocateControl feuert manchmal ~200-300ms nach Mode-Wechsel
    // und übersteuert unseren ersten fitBounds; der 2. Aufruf gewinnt dann.
    const t1 = window.setTimeout(doFit, 50);
    const t2 = window.setTimeout(doFit, 450);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [isShuttleMode, map, areas, activeAreaCode]);

  // Trail-Polylines verstecken im Shuttle-Mode.
  useEffect(() => {
    if (!map) return;
    const apply = () => {
      const visibility = isShuttleMode ? 'none' : 'visible';
      for (const layerId of TRAIL_LAYER_IDS) {
        try {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', visibility);
          }
        } catch {
          /* still */
        }
      }
    };
    apply();
    map.on('styledata', apply);
    map.on('idle', apply);
    return () => {
      map.off('styledata', apply);
      map.off('idle', apply);
      try {
        for (const layerId of TRAIL_LAYER_IDS) {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'visible');
          }
        }
      } catch {
        /* noop */
      }
    };
  }, [map, isShuttleMode]);

  // Runs der aktiven Area filtern. activeRuns ist gemerkt damit
  // useEffect-Deps stable bleiben.
  const activeRuns = useMemo(() => {
    if (!activeAreaCode) return [];
    return allRuns.filter((r) => r.shuttleAreaCode === activeAreaCode);
  }, [allRuns, activeAreaCode]);

  // Wave 14dt-fix4: Default-Preview = erste Card sobald activeRuns geladen.
  // Reset auf null wenn Area gewechselt oder Mode verlassen wird.
  useEffect(() => {
    if (activeRuns.length === 0) {
      setPreviewRunId(null);
      return;
    }
    // Wenn der aktuelle previewRunId nicht mehr in der Liste ist, auf
    // den ersten Run zurücksetzen.
    setPreviewRunId((current) => {
      if (current && activeRuns.some((r) => r.id === current)) return current;
      return activeRuns[0]!.id;
    });
  }, [activeRuns]);

  // Carousel-Scroll → Preview-Detect. Bei jedem Scroll-Event ermitteln wir
  // welche Card am nächsten zur Container-Mitte liegt und setzen sie als
  // Preview. requestAnimationFrame drosselt das (max 1× pro Frame).
  useEffect(() => {
    const ul = carouselRef.current;
    if (!ul) return;
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const containerRect = ul.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;
        let bestId: string | null = null;
        let bestDist = Infinity;
        for (const child of Array.from(ul.children) as HTMLElement[]) {
          const r = child.getBoundingClientRect();
          const childCenter = r.left + r.width / 2;
          const dist = Math.abs(childCenter - containerCenter);
          if (dist < bestDist) {
            bestDist = dist;
            bestId = child.dataset.runId ?? null;
          }
        }
        if (bestId) setPreviewRunId(bestId);
      });
    };
    ul.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      ul.removeEventListener('scroll', onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [activeRuns]);

  // Polylines der activeRuns als GeoJSON-Source + Line-Layer auf der Map.
  // Re-apply auf 'styledata' damit Style-Swaps (sat ↔ outdoor) den Layer
  // nicht verlieren.
  useEffect(() => {
    if (!map || !isShuttleMode) return;
    const apply = () => {
      const features = activeRuns.map((r) => ({
        type: 'Feature' as const,
        properties: {
          id: r.id,
          name: r.name,
          // Wave 14dt-fix4: isPreview-Flag wird im Layer-Paint genutzt um
          // den im Carousel-zentrierten Run hervorzuheben (orange, dicker).
          isPreview: r.id === previewRunId,
        },
        geometry: {
          type: 'LineString' as const,
          coordinates: r.track.points.map((p) => [p.lon, p.lat] as [number, number]),
        },
      }));
      const geojson = { type: 'FeatureCollection' as const, features };

      try {
        const existing = map.getSource(ACTIVE_RUNS_SOURCE) as
          | maplibregl.GeoJSONSource
          | undefined;
        if (existing) {
          existing.setData(geojson);
        } else {
          map.addSource(ACTIVE_RUNS_SOURCE, { type: 'geojson', data: geojson });
        }

        if (!map.getLayer(ACTIVE_RUNS_LAYER_CASING)) {
          map.addLayer({
            id: ACTIVE_RUNS_LAYER_CASING,
            type: 'line',
            source: ACTIVE_RUNS_SOURCE,
            paint: {
              'line-color': '#ffffff',
              // Wave 14dt-fix4: Preview-Casing breiter → der orange Stroke
              // sitzt sauber gerahmt im weißen Halo.
              'line-width': [
                'case',
                ['boolean', ['get', 'isPreview'], false],
                10,
                7,
              ] as unknown as number,
              'line-opacity': 0.85,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }
        if (!map.getLayer(ACTIVE_RUNS_LAYER)) {
          map.addLayer({
            id: ACTIVE_RUNS_LAYER,
            type: 'line',
            source: ACTIVE_RUNS_SOURCE,
            paint: {
              // Wave 14dt-fix4: Preview-Run in orange (matched RIDE-Button),
              // restliche Runs in sky-600 (matched Badges).
              'line-color': [
                'case',
                ['boolean', ['get', 'isPreview'], false],
                '#f97316', // orange-500
                '#0284c7', // sky-600
              ] as unknown as string,
              'line-width': [
                'case',
                ['boolean', ['get', 'isPreview'], false],
                6,
                4,
              ] as unknown as number,
              'line-opacity': 0.95,
            },
            layout: { 'line-cap': 'round', 'line-join': 'round' },
          });
        }
      } catch (err) {
        console.warn('[shuttle] apply run layer failed', err);
      }
    };
    apply();
    map.on('styledata', apply);
    return () => {
      map.off('styledata', apply);
      // Layer + Source aufräumen wenn der Effect re-runs (Area-Wechsel
      // oder Mode-Exit). Source bleibt für 1 Frame in MapLibre auch nach
      // removeLayer — getSource-Check guards das.
      try {
        if (map.getLayer(ACTIVE_RUNS_LAYER)) map.removeLayer(ACTIVE_RUNS_LAYER);
        if (map.getLayer(ACTIVE_RUNS_LAYER_CASING)) map.removeLayer(ACTIVE_RUNS_LAYER_CASING);
        if (map.getSource(ACTIVE_RUNS_SOURCE)) map.removeSource(ACTIVE_RUNS_SOURCE);
      } catch {
        /* noop */
      }
    };
    // Wave 14dt-fix4: previewRunId als Dep — bei Carousel-Swipe wird die
    // GeoJSON-Source neu mit aktualisierten isPreview-Flags geschrieben.
  }, [map, isShuttleMode, activeRuns, previewRunId]);

  // fitBounds auf die Run-Polylines wenn eine Area aktiviert wird.
  useEffect(() => {
    if (!map || activeRuns.length === 0) return;
    const lons: number[] = [];
    const lats: number[] = [];
    for (const r of activeRuns) {
      for (const p of r.track.points) {
        lons.push(p.lon);
        lats.push(p.lat);
      }
    }
    if (lons.length < 2) return;
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    try {
      map.fitBounds(
        [
          [minLon, minLat],
          [maxLon, maxLat],
        ],
        {
          // Bottom-Padding lässt Platz für die Card-Carousel. Top-Padding
          // klein für den Mode-Header.
          padding: { top: 100, bottom: 220, left: 30, right: 30 },
          duration: 800,
          maxZoom: 15,
        },
      );
    } catch (err) {
      console.warn('[shuttle] fitBounds failed', err);
    }
  }, [map, activeRuns]);

  const handleAreaSelect = (area: ShuttleArea) => {
    if (area.code === activeAreaCode) {
      // Tap auf das schon aktive Badge → deselect (zurück zum Overview)
      setActiveAreaCode(null);
      return;
    }
    setActiveAreaCode(area.code);
    // KEIN flyTo hier — der fitBounds-Effect kümmert sich um die Kamera
    // sobald activeRuns gefüllt sind.
  };

  const exitShuttleMode = () => {
    navigate('/', { replace: true });
  };

  if (!isShuttleMode) return null;

  const activeArea = activeAreaCode
    ? areas.find((a) => a.code === activeAreaCode) ?? null
    : null;

  return (
    <>
      <ShuttleAreaLayer
        map={map}
        areas={areas}
        onSelect={handleAreaSelect}
        activeCode={activeAreaCode}
      />

      {/* Wave 14dt-fix6: Mode-Info + Exit-X als EIN kombiniertes Widget
          rechts oben. Vorher kollidierte die zentrierte Pille mit dem
          Style-Switcher links oben — User-Feedback: "schieb sie nach
          rechts und mach das rote kreuz teil davon, dass die UI passt".
          Layout: sky-Pille mit Text + roter X-Button als Suffix, beide
          in einer Pill-Shape, beide tappable. */}
      <div
        className="absolute z-30 flex items-center gap-1 bg-sky-600 rounded-full pl-3 pr-1 py-1 shadow-md pointer-events-auto"
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)',
          right: '0.75rem',
        }}
      >
        <span className="text-white text-xs font-semibold whitespace-nowrap">
          DH mode
          {loading && <span className="text-white/70 ml-1">· loading…</span>}
          {!loading && areas.length > 0 && (
            <span className="text-white/75 ml-1">· {areas.length}</span>
          )}
        </span>
        <button
          type="button"
          onClick={exitShuttleMode}
          className="bg-red-600 hover:bg-red-500 active:bg-red-700 text-white font-bold rounded-full w-7 h-7 flex items-center justify-center text-base shadow-sm transition-colors"
          aria-label="Exit DH/Shuttle mode"
          style={{ touchAction: 'manipulation' }}
        >
          ×
        </button>
      </div>

      {/* Bottom-Carousel mit Run-Cards (Phase 3c).
          Sichtbar wenn eine Area aktiv ist. Bei 0 zugewiesenen Runs:
          Empty-Hint statt Crash. */}
      {activeArea && (
        <div
          className="absolute left-0 right-0 z-30 pointer-events-none"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }}
        >
          {/* Header über der Carousel: Area-Code + Name + Close-X. */}
          <div className="px-3 mb-2 flex items-center gap-2 pointer-events-auto">
            <div className="w-9 h-9 rounded-full bg-sky-600 text-white font-bold flex items-center justify-center text-sm border-2 border-white shrink-0 shadow-md">
              {activeArea.code}
            </div>
            <div
              className="flex-1 min-w-0 rounded-lg px-3 py-1.5 shadow-md"
              style={DARK_PANEL_BG}
            >
              <div className="font-semibold text-sm truncate" style={{ color: '#ffffff' }}>
                {activeArea.name}
              </div>
              <div className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.7)' }}>
                {activeRuns.length} run{activeRuns.length === 1 ? '' : 's'}
                {activeArea.description && ` · ${activeArea.description}`}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setActiveAreaCode(null)}
              className="shrink-0 text-lg w-9 h-9 rounded-lg flex items-center justify-center shadow-md"
              style={{ ...DARK_PANEL_BG, color: 'rgba(255,255,255,0.8)' }}
              aria-label="Close area"
            >
              ×
            </button>
          </div>

          {/* Horizontal-Carousel — snap-x für Tinder-style Swipe.
              Cards = 80vw breit, Gap 8px. */}
          {activeRuns.length === 0 ? (
            <div
              className="mx-3 border border-sky-500/40 rounded-xl px-4 py-5 text-center pointer-events-auto shadow-md"
              style={DARK_PANEL_BG}
            >
              <div className="text-sm font-medium mb-1" style={{ color: '#ffffff' }}>
                No runs assigned to {activeArea.code} yet
              </div>
              <div className="text-xs" style={{ color: 'rgba(255,255,255,0.65)' }}>
                Admin can assign runs to this area in the run-edit form.
              </div>
            </div>
          ) : (
            <ul
              ref={carouselRef}
              className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 pointer-events-auto list-none m-0"
              style={{
                scrollbarWidth: 'none',
                // Wave 14dt-fix4: Padding so dass Cards mit snap-center
                // wirklich in der Viewport-Mitte landen (sonst kann die
                // erste/letzte Card nicht weit genug nach rechts/links
                // scrollen). 10vw = ~halbe Card-Außenmarge.
                paddingLeft: '10vw',
                paddingRight: '10vw',
                scrollSnapType: 'x mandatory',
              }}
            >
              {activeRuns.map((run) => (
                <li
                  key={run.id}
                  data-run-id={run.id}
                  className="snap-center shrink-0"
                  style={{ width: 'calc(80vw - 24px)', maxWidth: '320px' }}
                >
                  <RunCard run={run} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

interface RunCardProps {
  run: CachedRun;
}

function RunCard({ run }: RunCardProps) {
  // Wave 14dt-fix3: <Link> statt <button> + onClick(navigate). In einem
  // horizontal-scrollenden snap-x-Container schluckt iOS Safari den
  // touch-end-Event manchmal als Scroll-Geste, der Click feuert nicht.
  // <Link> nutzt natives Browser-Tap-Handling und ist robuster.
  // `touchAction: 'manipulation'` zusätzlich, um Double-Tap-Zoom-Delay
  // und mögliche Gesten-Hijacks zu vermeiden.
  const runPath = `/runs/${encodeURIComponent(run.id)}`;
  return (
    <div
      className="border border-sky-500/40 rounded-xl px-3 py-3 shadow-md flex flex-col gap-2"
      style={DARK_PANEL_BG}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="w-1.5 h-9 rounded-sm shrink-0"
          style={{
            background: difficultySwatchBackground(run.difficulty),
            boxShadow: '0 0 0 1px rgba(255,255,255,0.35)',
          }}
          aria-label={DIFFICULTY_LABEL[run.difficulty as Difficulty]}
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate" style={{ color: '#ffffff' }}>
            {run.name}
          </div>
          <div
            className="text-[11px] tabular-nums truncate"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {formatKm(run.stats.lengthMeters)} · ↑ {Math.round(run.stats.gain)} m
            · ↓ {Math.round(run.stats.loss)} m
          </div>
        </div>
      </div>
      {/* 2-Spalten-Grid: SHOW (sky, links) + RIDE (orange, rechts). SHOW
          öffnet die existierende RunDetailPage (= NAVI-list-tap-Verhalten);
          RIDE setzt zusätzlich ?ride=1 als Hint für RunDetailPage einen
          späteren Auto-Ride-Flow zu triggern (oder zumindest visuell
          unterscheidbar zu sein). */}
      <div className="grid grid-cols-2 gap-2">
        <Link
          to={runPath}
          className="bg-sky-600 hover:bg-sky-500 active:bg-sky-700 text-white font-bold text-sm rounded-lg py-2 transition-colors flex items-center justify-center gap-1.5 no-underline"
          style={{ touchAction: 'manipulation' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          SHOW
        </Link>
        <Link
          to={`${runPath}?ride=1`}
          className="bg-orange-500 hover:bg-orange-400 active:bg-orange-600 text-white font-bold text-sm rounded-lg py-2 transition-colors flex items-center justify-center gap-1.5 no-underline"
          style={{ touchAction: 'manipulation' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
            <path d="M5 3l14 9-14 9V3z" />
          </svg>
          RIDE
        </Link>
      </div>
    </div>
  );
}
